import { getCachedCustomerTiers } from "../../../app/api/getCustomerTiers";
import { getTicketEarnRate } from "../../../app/api/getCachedTiersConfig";
import { BaseAction } from "../../../core/actions/BaseAction";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { ILoyaltyTier } from "../../../Orchestrator/Types/Index";
import { IEarnPointsRequest } from "../../../Orchestrator/Types/Interface/ActionParams/EarnPointsActionParams";
import { findDynamicProduct } from "../../../Orchestrator/Types/Types/TrainTypes";
import { TrainTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { findCheapestAvailable } from "../../../Lib/Utils/productOrderTag";

class GetEarnPointsAction extends BaseAction<void, IEarnPointsRequest> {
    override async run(): Promise<void> {
        try {
            const { selectedTripType, train, type, isChangeDate, loyaltyProgramResponse } = this.params!;

            const isAnonymous: boolean = getRecoilStateValue("isAnonymousStateAtom") as boolean;
            const isBookReturnJourney: boolean = getRecoilStateValue("isBookReturnJourneyAtom") as boolean;

            // Skip for anonymous users, change-date, and book return journey
            if (
                (isAnonymous && isAnonymous === true)
                || (isChangeDate && isChangeDate === true)
                || (isBookReturnJourney && isBookReturnJourney === true)
            ) {
                updateRecoilState<number>("earnPointsAtom", 0);
                updateRecoilState<number>("earnExtraPointsAtom", 0);
                updateRecoilState<number>("earnPointsOutboundAtom", 0);
                updateRecoilState<number>("earnPointsInboundAtom", 0);
                return;
            }

            // Use cached tiers (pre-fetched on page load, deduped if still in-flight)
            const tiers = await getCachedCustomerTiers();

            // Fetch ticket earn rate (points per AED), session-cached via getCachedTiersConfig
            let ticketEarnRate = 1;
            try {
                ticketEarnRate = await getTicketEarnRate();
            } catch {
                console.warn("[GetEarnPointsAction] Failed to fetch ticket earn rate, falling back to 1 pt/AED");
            }

            if (tiers && tiers.length > 0) {
                const customerTier = loyaltyProgramResponse;
                if (customerTier) {
                    const customerTierData = customerTier?.loyaltyProgram?.tier?.name;
                    if (customerTierData) {
                        const tier = tiers.find((tier: ILoyaltyTier) => tier.name === customerTierData);
                        if (tier) {
                            let calculatedPoints: number;
                            if (selectedTripType === "premium") {
                                calculatedPoints = ticketEarnRate * (train?.priceInfo?.premium?.price ?? 0);
                                if(train?.priceInfo?.shuttle?.price !== undefined && train.priceInfo.shuttle.price > 0){
                                    calculatedPoints += ticketEarnRate * (train?.priceInfo?.shuttle?.price ?? 0);
                                }
                                updateRecoilState<number>("earnPointsAtom", calculatedPoints);
                                updateRecoilState<number>("earnExtraPointsAtom", 0);
                            }
                            else {
                                const selectedPrice = (train?.priceInfo as any)?.[selectedTripType ?? ""]?.price
                                    ?? findDynamicProduct(train?.priceInfo, selectedTripType ?? "")?.price
                                    ?? 0;
                                calculatedPoints = ticketEarnRate * selectedPrice;
                                if(train?.priceInfo?.shuttle?.price !== undefined && train.priceInfo.shuttle.price > 0 && selectedTripType !== "shuttle"){
                                    calculatedPoints += ticketEarnRate * (train?.priceInfo?.shuttle?.price ?? 0);
                                }
                                updateRecoilState<number>("earnPointsAtom", calculatedPoints);
                                // Derive cheapest premium by min-price across available
                                // products, NOT by array index. premiumProducts is sorted
                                // by CMS `order` tag (see compareByOrderThenPrice), so
                                // [0] may be a pinned non-cheapest product.
                                const cheapestPremium = findCheapestAvailable(
                                    train?.priceInfo?.premiumProducts,
                                    (p) => !p.isSoldOut,
                                    (p) => p.price ?? Infinity,
                                );
                                const cheapestPremiumPrice = cheapestPremium?.price ?? train?.priceInfo?.premium?.price ?? 0;
                                updateRecoilState<number>("earnExtraPointsAtom", cheapestPremiumPrice > 0 ? Math.max(0, ticketEarnRate * (cheapestPremiumPrice - selectedPrice)) : 0);
                            }

                            if (type === TrainTypeEnum.outbound) {
                                updateRecoilState<number>("earnPointsOutboundAtom", calculatedPoints);
                                updateRecoilState<number>("earnPointsInboundAtom", 0);
                                updateRecoilState<number>("earnPointsTotalAtom", calculatedPoints);
                            } else if (type === TrainTypeEnum.inbound) {
                                updateRecoilState<number>("earnPointsInboundAtom", calculatedPoints);
                                const outboundPoints = getRecoilStateValue<number>("earnPointsOutboundAtom", 0) ?? 0;
                                updateRecoilState<number>("earnPointsTotalAtom", outboundPoints + calculatedPoints);
                            }
                        }
                    }
                }
            }
        }

        catch (error: any | null) {
            console.error("Error in GetEarnPointsAction:", error);
        }
    }
}

export const getEarnPointsAction = new GetEarnPointsAction();
